import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { CommonLocale, WordManagerLocale } from "@/locales/localeid";
import { fmtMsg, ActionItem, MessageHelper } from "commonui";
import { Select, Input, Button, Icon, Tag, Alert, Checkbox } from "ant-design-vue";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { GridRef } from "@/components/wijmo-grid/grid/grid";
import WordManagerForm from "@/components/word-manager-form/word-manager-form.vue";
import { isNull, isUndefined } from "lodash";
import { LanguageSkillModel, VCSLevelModel } from "@/models/title-list-model";
import { WordManagerService } from "@/services/word-manager-service";
import { WordManagerModel, WordSpecVpcRespnseModel } from "@/models/word-manager-model";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import { disabledDueToAccessLevel, getRouteAccessLevel, getSkillSelected, isAnyVersionSelected } from "@/util/func";
import { AllLanguageSkill, AllLevel, NoneLevel } from "@/util/constants";
import { VersionGroupModule } from "@/modules/versiongroup";
import { AccessLevel } from "@/util/enum";

interface RequiredParams {
	levelId: number | null;
	serachText: string;
	languageSkills: number[];
	gsVersion: string | null;
	lsVersion: string | null;
	isVpcOnly: boolean;
}
@Component({
	components: {
		WordManagerForm,
		Select,
		Option: Select.Option,
		Grid,
		Input,
		InputSearch: Input.Search,
		Button,
		Icon,
		Tag,
		ActionItem,
		Alert,
		Checkbox
	}
})
export default class WordManager extends Vue {
	@Ref() readonly gridCompRef!: GridRef;
	public gridLoading: boolean = false;
	public records: WordManagerModel[] = [];
	public commonLocale = CommonLocale;
	public isDrawerVisible = false;
	public wordId: number = 0;
	public readonly allLevel = AllLevel;
	public readonly noneLevel = NoneLevel;
	public readonly allLanguageSkill = AllLanguageSkill;
	public unitSelected: number = AllLevel;
	public searchValue: string = "";
	public wordInfo: WordManagerModel | null = null;
	public titleListModule = getModule(TitleListState);
	public skillSelected: number[] = [];
	public noVersion: boolean = false;
	public vpcOnly: boolean = false;
	private versionGroupModule = VersionGroupModule; // For watch need to create local reference
	public accessLevel = AccessLevel;

	created() {
		if (this.languageSkills.length > 0) {
			this.skillSelected = [this.allLanguageSkill];
		}
	}

	get levels(): VCSLevelModel[] {
		/*
		 If selected GrapeSeed version is None the select only LittleSeed units.
		 If selected LittleSeed version is None the select only GrapeSeed units.
		 If both GrapeSeed and LittleSeed are None then return empty array.
		 If both GrapeSeed and LittleSeed are selected then return all the units.
		*/
		if (VersionGroupModule.selectedGsVersion == "-1" && VersionGroupModule.selectedLsVersion != "-1") {
			return this.titleListModule.levels.filter((l) => l.curriculumType != 0);
		} else if (VersionGroupModule.selectedLsVersion == "-1" && VersionGroupModule.selectedGsVersion != "-1") {
			return this.titleListModule.levels.filter((l) => l.curriculumType != 1);
		} else if (VersionGroupModule.selectedGsVersion == "-1" && VersionGroupModule.selectedLsVersion == "-1") {
			return [];
		} else {
			return this.titleListModule.levels;
		}
	}

	onSkillChange(selectedValues) {
		this.skillSelected = getSkillSelected(selectedValues);
	}

	get languageSkills(): LanguageSkillModel[] {
		return this.titleListModule.languageSkills;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	mounted() {
		this.versionGroupModule.setDisabled(false);
	}

	destroyed() {
		this.versionGroupModule.setDisabled(true);
	}

	@Watch("versionGroupModule.selectedGsVersion", { immediate: true })
	@Watch("versionGroupModule.selectedLsVersion")
	isVersionUpdated() {
		if (isAnyVersionSelected()) {
			this.noVersion = false;
		} else {
			this.noVersion = true;
		}
		this.unitSelected = AllLevel;
	}

	get wordManagerLocale() {
		return WordManagerLocale;
	}

	localizedString(id: string, params?: {}) {
		return fmtMsg(id, params);
	}

	setGridLoading(value: boolean) {
		this.gridLoading = value;
	}

	getRequiredParams(): RequiredParams {
		const skills = this.skillSelected.includes(this.allLanguageSkill)
			? this.languageSkills.map((l) => l.id)
			: this.skillSelected;

		return {
			isVpcOnly: this.vpcOnly,
			levelId: this.unitSelected === AllLevel ? null : this.unitSelected,
			serachText: this.searchValue,
			languageSkills: skills,
			gsVersion: VersionGroupModule.selectedGsVersion !== "-1" ? VersionGroupModule.selectedGsVersion : null,
			lsVersion: VersionGroupModule.selectedLsVersion !== "-1" ? VersionGroupModule.selectedLsVersion : null
		};
	}

	getWordsInfo() {
		this.setGridLoading(true);
		WordManagerService.getWordsInfo(this.getRequiredParams())
			.then((data) => {
				this.records = data;
				this.setGridLoading(false);
			})
			.catch((ex) => {
				this.setGridLoading(false);
			});
	}

	get exportOptions() {
		return {
			filename: fmtMsg(WordManagerLocale.WordManagerExportTitle)
		};
	}

	get wordManagerDefaultSort() {
		return {
			column: "word",
			ascending: true
		};
	}

	setDrawerVisibility(value) {
		this.isDrawerVisible = value;
	}

	refreshWordList() {
		this.getWordsInfo();
	}

	closeFormDrawer(isRefreshRequired) {
		if (isRefreshRequired) {
			this.refreshWordList();
		}
		this.setDrawerVisibility(false);
		this.wordInfo = null;
	}

	onDisplayClick() {
		this.getWordsInfo();
	}

	onAddWordClick() {
		this.wordId = 0;
		this.wordInfo = null;
		this.setDrawerVisibility(true);
	}

	onWordClick(item: WordManagerModel) {
		this.wordId = item.id;

		const wordDetails =
			item.id > 0 ? this.records.find((w) => w.id === item.id) : this.records.find((w) => w.word === item.word);

		if (!isUndefined(wordDetails)) {
			this.wordInfo = wordDetails;
		}

		this.setDrawerVisibility(true);
	}

	get vpclevels(): VCSLevelModel[] {
		/*
		 If selected GrapeSeed version is None the select only LittleSeed units.
		 If selected LittleSeed version is None the select only GrapeSeed units.
		 If both GrapeSeed and LittleSeed are selected or both are none then return all the units.
		*/
		if (VersionGroupModule.selectedGsVersion == "-1" && VersionGroupModule.selectedLsVersion != "-1") {
			return this.titleListModule.levels.filter((l) => l.curriculumType != 0);
		} else if (VersionGroupModule.selectedLsVersion == "-1" && VersionGroupModule.selectedGsVersion != "-1") {
			return this.titleListModule.levels.filter((l) => l.curriculumType != 1);
		} else {
			return this.titleListModule.levels;
		}
	}
}
